import { toStringBigDecimal } from '@0xtorch/big-decimal'
import type { Action } from '@0xtorch/core'
import { createEvmActionSource, isHex } from '@pkg/evm'
import { createSolanaActionSource } from '@pkg/solana'
import {
  ApiSecretRequiredException,
  AssetNotFoundException,
  SubTypeRequiredException,
} from '../../errors'
import type { FetchApiData } from '../../types'
import { createGetAsset } from '../../utils/createGetAsset'
import {
  bitbankAccountCategory,
  networkEvmChainIds,
  solanaNetwork,
} from '../constants'
import { createClient } from '../createClient'
import { responseSchema } from './schema'

export const fetchApiData: FetchApiData = async ({
  apiKey,
  apiSecret,
  assets,
  config,
  from,
  createHeaders,
  proxy,
  subType,
  symbolAssetMap,
  to,
}) => {
  if (!apiSecret) {
    throw new ApiSecretRequiredException()
  }
  if (!subType) {
    throw new SubTypeRequiredException()
  }

  const getAsset = createGetAsset({ assets, symbolAssetMap })

  const client = createClient({
    apiKey,
    apiSecret,
    createHeaders,
    proxy,
  })

  const response = await client.run({
    method: 'GET',
    path: '/v1/user/withdrawal_history',
    query: {
      asset: subType,
      count: config.limit.toString(),
      ...(from ? { since: from.toString() } : {}),
      ...(to ? { end: to.toString() } : {}),
    },
    responseSchema,
  })

  // 昇順 sort by requested_at
  const sortedWithdrawals = response.data.withdrawals.sort(
    (a, b) => a.requested_at - b.requested_at,
  )

  // データ処理
  let stopped = false
  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const sources: string[] = []
  const actions: Action[] = []

  for (const item of sortedWithdrawals) {
    // Skip rejected, canceled, or timed out withdrawals
    if (
      item.status === 'REJECTED' ||
      item.status === 'CANCELED' ||
      item.status === 'CONFIRM_TIMEOUT'
    ) {
      continue
    }

    // Stop processing if withdrawal is not done
    if (item.status !== 'DONE') {
      stopped = true
      break
    }

    // Update fetchedFrom and fetchedTo
    fetchedFrom =
      fetchedFrom === undefined
        ? item.requested_at
        : Math.min(fetchedFrom, item.requested_at)
    fetchedTo =
      fetchedTo === undefined
        ? item.requested_at
        : Math.max(fetchedTo, item.requested_at)

    // Create fee action if fee > 0
    if (item.fee.value > 0n) {
      const asset = getAsset(item.asset)
      if (!asset) {
        throw new AssetNotFoundException(item.asset)
      }
      if (asset) {
        actions.push({
          comment: `Withdraw ${toStringBigDecimal(item.amount)} [${item.asset.toUpperCase()}] | network: ${item.network} | tx: ${item.txid}`,
          evidence: 'system-rule',
          order: 0,
          source: `${bitbankAccountCategory}_${config.type}_${item.uuid}`,
          type: 'fee',
          timestamp: item.requested_at,
          transfers: [
            {
              asset,
              amount: item.fee,
              direction: 'out',
            },
          ],
        })
      }
    }

    // Create EVM source if applicable
    if (item.network && item.txid) {
      const evmChainId = networkEvmChainIds[item.network]
      if (evmChainId && isHex(item.txid)) {
        sources.push(
          createEvmActionSource({
            chainId: evmChainId,
            transactionHash: item.txid,
          }),
        )
      }
    }

    // Create Solana source if applicable
    if (item.network === solanaNetwork && item.txid) {
      sources.push(createSolanaActionSource({ signature: item.txid }))
    }
  }

  // Sort actions by timestamp in descending order
  const sortedActions = actions.sort((a, b) => b.timestamp - a.timestamp)

  return {
    actions: sortedActions,
    count: response.data.withdrawals.length,
    from: fetchedFrom,
    sources,
    stopped,
    to: fetchedTo,
  }
}
